/**
 * Small visual-inspection panel for environment work.
 *
 * This is deliberately separate from the game HUD: benchmark captures never mount it,
 * and the default view keeps it collapsed to a single unobtrusive ENV button.
 */

import type { DebugApi } from './api';
import type { WeatherPresetName } from '../sim/weather';

interface PresetOption {
  value: WeatherPresetName;
  label: string;
}

const PRESETS: PresetOption[] = [
  { value: 'clearDay', label: 'Clear day' },
  { value: 'overcastDay', label: 'Atlantic overcast' },
  { value: 'fogBank', label: 'Dense fog bank' },
  { value: 'dusk', label: 'Dusk' },
  { value: 'night', label: 'Night' },
  { value: 'nightStorm', label: 'Night storm' },
  { value: 'storm', label: 'Storm' },
  { value: 'severeStorm', label: 'Severe storm' },
  { value: 'dawnFog', label: 'Foggy dawn' },
];

const TIME_MODES = [
  { value: 5.8, mode: 'dawn', label: 'Dawn' },
  { value: 11, mode: 'day', label: 'Day' },
  { value: 19.5, mode: 'dusk', label: 'Dusk' },
  { value: 23, mode: 'night', label: 'Night' },
] as const;

function timeMode(hours: number): string {
  if (hours >= 4.5 && hours < 7.5) return 'dawn';
  if (hours >= 7.5 && hours < 18.5) return 'day';
  if (hours >= 18.5 && hours < 21) return 'dusk';
  return 'night';
}

function formatTime(hours: number): string {
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60) % 24;
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export interface EnvironmentPanelOptions {
  initiallyOpen?: boolean;
}

export function mountEnvironmentPanel(
  api: DebugApi,
  options: EnvironmentPanelOptions = {},
): () => void {
  const panel = document.createElement('aside');
  panel.className = 'env-inspector';
  panel.setAttribute('aria-label', 'Environment inspector');
  panel.innerHTML = `
    <button
      class="env-inspector__tab"
      type="button"
      aria-expanded="false"
      aria-controls="envInspectorBody"
      title="Environment inspector (D)"
    >
      <span class="env-inspector__pulse" aria-hidden="true"></span>
      ENV
    </button>
    <section class="env-inspector__body" id="envInspectorBody" aria-hidden="true">
      <header class="env-inspector__header">
        <div>
          <span class="env-inspector__eyebrow">VISUAL DEBUG</span>
          <h2>Environment</h2>
        </div>
        <button class="env-inspector__close" type="button" aria-label="Close environment inspector">×</button>
      </header>
      <label class="env-inspector__field" for="envWeatherPreset">
        <span>Weather</span>
        <select id="envWeatherPreset">
          ${PRESETS.map(({ value, label }) => `<option value="${value}">${label}</option>`).join('')}
        </select>
      </label>
      <div class="env-inspector__field">
        <span>Light</span>
        <div class="env-inspector__segments" role="group" aria-label="Time of day">
          ${TIME_MODES.map(({ value, mode, label }) => `
            <button type="button" data-time="${value}" data-time-mode="${mode}" aria-pressed="false">${label}</button>
          `).join('')}
        </div>
      </div>
      <div class="env-inspector__status" aria-live="polite">
        <span class="env-inspector__live"><i aria-hidden="true"></i>LIVE</span>
        <span class="env-inspector__readout"></span>
      </div>
      <p class="env-inspector__hint"><kbd>D</kbd> toggles this panel</p>
    </section>
  `;

  const tab = panel.querySelector<HTMLButtonElement>('.env-inspector__tab');
  const close = panel.querySelector<HTMLButtonElement>('.env-inspector__close');
  const body = panel.querySelector<HTMLElement>('.env-inspector__body');
  const select = panel.querySelector<HTMLSelectElement>('#envWeatherPreset');
  const readout = panel.querySelector<HTMLElement>('.env-inspector__readout');
  const timeButtons = Array.from(panel.querySelectorAll<HTMLButtonElement>('[data-time]'));

  if (!tab || !close || !body || !select || !readout) {
    throw new Error('environment inspector markup is incomplete');
  }

  let open = false;
  const setOpen = (next: boolean): void => {
    open = next;
    panel.classList.toggle('is-open', open);
    tab.setAttribute('aria-expanded', String(open));
    body.setAttribute('aria-hidden', String(!open));
  };

  const sync = (): void => {
    const weather = api.getWeather();
    const activeMode = timeMode(weather.timeOfDay);
    for (const button of timeButtons) {
      button.setAttribute('aria-pressed', String(button.dataset.timeMode === activeMode));
    }
    readout.textContent = `Sea ${weather.seaState.toFixed(1)} · Fog ${Math.round(weather.fogDensity * 100)}% · ${formatTime(weather.timeOfDay)}`;
  };

  const onToggle = (): void => setOpen(!open);
  const onClose = (): void => setOpen(false);
  const onPreset = (): void => {
    api.setWeather(select.value as WeatherPresetName);
    sync();
  };
  const onTime = (event: Event): void => {
    const target = event.currentTarget as HTMLButtonElement;
    api.setTimeOfDay(Number(target.dataset.time));
    sync();
  };
  const onKey = (event: KeyboardEvent): void => {
    const target = event.target;
    if (
      target instanceof HTMLInputElement
      || target instanceof HTMLSelectElement
      || target instanceof HTMLTextAreaElement
    ) return;
    if (event.key.toLowerCase() === 'd') onToggle();
  };

  tab.addEventListener('click', onToggle);
  close.addEventListener('click', onClose);
  select.addEventListener('change', onPreset);
  for (const button of timeButtons) button.addEventListener('click', onTime);
  window.addEventListener('keydown', onKey);

  select.value = 'overcastDay';
  sync();
  setOpen(options.initiallyOpen ?? false);
  document.body.append(panel);

  return () => {
    tab.removeEventListener('click', onToggle);
    close.removeEventListener('click', onClose);
    select.removeEventListener('change', onPreset);
    for (const button of timeButtons) button.removeEventListener('click', onTime);
    window.removeEventListener('keydown', onKey);
    panel.remove();
  };
}
